import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Fixture {
  id: number;
  date: string;
  time: string;
  ageGroup: string;
  teamA: string;
  teamB: string;
  ground: string;
  status: string;
  result: string | null;
  div?: string;
}

type AgeFilter = "All" | "U11" | "U13" | "U15" | "U17";
type Format = "T20" | "40-over" | "T10";

// ─── Static data ──────────────────────────────────────────────────────────────

const AGE_GROUPS = ["All", "U11", "U13", "U15", "U17"] as const;
const SELECTABLE_AGE_GROUPS = ["U11", "U13", "U15", "U17"] as const;
const ALL_GROUNDS = ["Frisco Cricket Ground", "McKinney Sports Complex", "Allen Cricket Ground"];
const TIMES = ["9:00 AM", "1:00 PM", "5:00 PM"];

const TEAMS_BY_AGE_GROUP: Record<string, string[]> = {
  U11: ["Northside U11", "Eastside U11", "Westfield U11", "Central U11"],
  U13: ["Northside U13", "Eastside U13", "Westfield U13", "Central U13", "Northside B U13"],
  U15: ["Eastside U15", "Westfield U15", "Central U15"],
  U17: ["Northside U17", "Eastside U17"],
};

const INITIAL_FIXTURES: Fixture[] = [
  { id: 1, date: "2026-06-28", time: "9:00 AM",  ageGroup: "U13", teamA: "Northside U13",    teamB: "Westfield U13",   ground: "Frisco Cricket Ground",   status: "upcoming",  result: null },
  { id: 2, date: "2026-06-28", time: "1:00 PM",  ageGroup: "U11", teamA: "Central U11",      teamB: "Eastside U11",    ground: "McKinney Sports Complex", status: "upcoming",  result: null },
  { id: 3, date: "2026-06-22", time: "10:00 AM", ageGroup: "U15", teamA: "Eastside U15",     teamB: "Westfield U15",   ground: "Frisco Cricket Ground",   status: "completed", result: "Eastside U15 won by 34 runs" },
  { id: 4, date: "2026-06-22", time: "2:00 PM",  ageGroup: "U13", teamA: "Central U13",      teamB: "Northside B U13", ground: "Allen Cricket Ground",    status: "completed", result: "Central won by 5 wickets" },
  { id: 5, date: "2026-07-05", time: "9:00 AM",  ageGroup: "U17", teamA: "TBD",              teamB: "TBD",             ground: "TBD",                     status: "scheduled", result: null },
];

const STATUS_BADGE: Record<string, string> = {
  upcoming:  "bg-blue-100 text-blue-700 border-blue-300",
  completed: "bg-green-100 text-green-700 border-green-300",
  scheduled: "bg-gray-100 text-gray-600 border-gray-300",
};

// ─── Fixture generation ───────────────────────────────────────────────────────

type DivTeams = Record<string, { divA: string[]; divB: string[] }>;

function buildInitialDivTeams(ageGroups: string[]): DivTeams {
  const result: DivTeams = {};
  for (const ag of ageGroups) {
    const teams = TEAMS_BY_AGE_GROUP[ag] ?? [];
    const mid = Math.ceil(teams.length / 2);
    result[ag] = { divA: teams.slice(0, mid), divB: teams.slice(mid) };
  }
  return result;
}

function generateFixtures(
  ageGroups: string[],
  divisionSplit: boolean,
  divTeams: DivTeams,
  startDate: string,
  grounds: string[],
  gamesPerDay: number,
): Fixture[] {
  const pairs: { ageGroup: string; teamA: string; teamB: string; div?: string }[] = [];
  const usedGrounds = grounds.length > 0 ? grounds : ALL_GROUNDS;

  for (const ag of ageGroups) {
    if (divisionSplit && divTeams[ag]) {
      const { divA, divB } = divTeams[ag];
      for (let i = 0; i < divA.length; i++)
        for (let j = i + 1; j < divA.length; j++)
          pairs.push({ ageGroup: ag, teamA: divA[i], teamB: divA[j], div: "A" });
      for (let i = 0; i < divB.length; i++)
        for (let j = i + 1; j < divB.length; j++)
          pairs.push({ ageGroup: ag, teamA: divB[i], teamB: divB[j], div: "B" });
    } else {
      const teams = TEAMS_BY_AGE_GROUP[ag] ?? [];
      for (let i = 0; i < teams.length; i++)
        for (let j = i + 1; j < teams.length; j++)
          pairs.push({ ageGroup: ag, teamA: teams[i], teamB: teams[j] });
    }
  }

  const slotsPerDay = gamesPerDay * usedGrounds.length;
  const dateObj = new Date(startDate + "T00:00:00");
  let slotsThisDay = 0;

  return pairs.map((p, i) => {
    if (slotsThisDay >= slotsPerDay) {
      dateObj.setDate(dateObj.getDate() + 1);
      slotsThisDay = 0;
    }
    const groundIdx = slotsThisDay % usedGrounds.length;
    const timeIdx = Math.floor(slotsThisDay / usedGrounds.length) % TIMES.length;
    const dateStr = dateObj.toISOString().split("T")[0];
    slotsThisDay++;
    return {
      id: i + 100,
      date: dateStr,
      time: TIMES[timeIdx],
      ageGroup: p.ageGroup,
      teamA: p.teamA,
      teamB: p.teamB,
      ground: usedGrounds[groundIdx],
      status: "scheduled",
      result: null,
      div: p.div,
    };
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function HubGamesScheduleDemo() {
  const { toast } = useToast();
  const [fixtures, setFixtures] = useState<Fixture[]>(INITIAL_FIXTURES);

  const [searchParams, setSearchParams] = useSearchParams();
  const paramAge = searchParams.get("ageGroup") as AgeFilter | null;
  const [ageFilter, setAgeFilter] = useState<AgeFilter>(
    AGE_GROUPS.includes(paramAge as AgeFilter) ? (paramAge as AgeFilter) : "All"
  );

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<string[]>(["U13"]);
  const [format, setFormat] = useState<Format>("T20");
  const [divisionSplit, setDivisionSplit] = useState(false);

  // Step 2
  const [divTeams, setDivTeams] = useState<DivTeams>({});

  // Step 3
  const [startDate, setStartDate] = useState("2026-07-05");
  const [endDate, setEndDate] = useState("2026-07-20");
  const [selectedGrounds, setSelectedGrounds] = useState<string[]>([...ALL_GROUNDS]);
  const [gamesPerDay, setGamesPerDay] = useState(2);

  // Step 4
  const [previewFixtures, setPreviewFixtures] = useState<Fixture[]>([]);

  function openWizard() {
    setStep(1);
    setSelectedAgeGroups(["U13"]);
    setFormat("T20");
    setDivisionSplit(false);
    setStartDate("2026-07-05");
    setEndDate("2026-07-20");
    setSelectedGrounds([...ALL_GROUNDS]);
    setGamesPerDay(2);
    setWizardOpen(true);
  }

  function handleNext() {
    if (step === 1) {
      if (divisionSplit) {
        setDivTeams(buildInitialDivTeams(selectedAgeGroups));
        setStep(2);
      } else {
        setStep(3);
      }
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setPreviewFixtures(generateFixtures(selectedAgeGroups, divisionSplit, divTeams, startDate, selectedGrounds, gamesPerDay));
      setStep(4);
    }
  }

  function handleBack() {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(divisionSplit ? 2 : 1);
    else if (step === 4) setStep(3);
  }

  function handleRegenerate() {
    setPreviewFixtures(generateFixtures(selectedAgeGroups, divisionSplit, divTeams, startDate, selectedGrounds, gamesPerDay));
  }

  function handleConfirm() {
    setFixtures(previewFixtures);
    setWizardOpen(false);
    toast({ title: "Fixtures confirmed", description: `${previewFixtures.length} games scheduled.` });
  }

  function toggleAgeGroup(ag: string) {
    setSelectedAgeGroups((prev) =>
      prev.includes(ag) ? prev.filter((x) => x !== ag) : [...prev, ag]
    );
  }

  function toggleGround(g: string) {
    setSelectedGrounds((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  }

  function moveToDiv(ag: string, player: string, from: "divA" | "divB") {
    setDivTeams((prev) => {
      const cur = prev[ag] ?? { divA: [], divB: [] };
      if (from === "divA") {
        return { ...prev, [ag]: { divA: cur.divA.filter((p) => p !== player), divB: [...cur.divB, player] } };
      } else {
        return { ...prev, [ag]: { divA: [...cur.divA, player], divB: cur.divB.filter((p) => p !== player) } };
      }
    });
  }

  function selectAge(ag: AgeFilter) {
    setAgeFilter(ag);
    if (ag === "All") setSearchParams({});
    else setSearchParams({ ageGroup: ag });
  }

  const filtered = ageFilter === "All" ? fixtures : fixtures.filter((f) => f.ageGroup === ageFilter);
  const upcoming  = filtered.filter((f) => f.status === "upcoming" || f.status === "scheduled");
  const completed = filtered.filter((f) => f.status === "completed");

  const stepLabel =
    step === 1 ? "Step 1 — Age Groups & Format" :
    step === 2 ? "Step 2 — Division Assignment" :
    step === 3 ? "Step 3 — Schedule Constraints" :
                 "Step 4 — Preview & Confirm";

  const canNext1 = selectedAgeGroups.length > 0;
  const canNext3 = selectedGrounds.length > 0;

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-games-schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">Games & Schedule</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              size="sm"
              className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
              onClick={openWizard}
              data-testid="btn-generate-fixtures"
            >
              Generate Fixtures
            </Button>
            <p className="text-xs text-gray-400">AI Fixture Generator with constraint satisfaction. Dallas Hub Phase 2.</p>
          </div>
        </div>

        {/* Age group filter */}
        <div className="flex flex-wrap gap-2" data-testid="age-filter-pills">
          {AGE_GROUPS.map((ag) => (
            <button
              key={ag}
              onClick={() => selectAge(ag)}
              data-testid={`age-pill-${ag.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                ageFilter === ag
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {ag}
            </button>
          ))}
        </div>

        {/* Upcoming */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Upcoming Games
            <span className="ml-2 text-xs font-normal text-gray-400 normal-case">({upcoming.length})</span>
          </h2>
          {upcoming.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">No upcoming games for this filter.</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map((f) => <FixtureCard key={f.id} fixture={f} />)}
            </div>
          )}
        </div>

        {/* Completed */}
        {completed.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Completed Games
              <span className="ml-2 text-xs font-normal text-gray-400 normal-case">({completed.length})</span>
            </h2>
            <div className="space-y-3">
              {completed.map((f) => <FixtureCard key={f.id} fixture={f} />)}
            </div>
          </div>
        )}
      </div>

      {/* ── Generate Fixtures Wizard ── */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="generate-fixtures-dialog">
          <DialogHeader>
            <DialogTitle>{stepLabel}</DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#1E2761]" : "bg-gray-200"}`}
              />
            ))}
          </div>

          {/* ── Step 1 ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Age Groups</p>
                <div className="flex flex-wrap gap-2">
                  {SELECTABLE_AGE_GROUPS.map((ag) => (
                    <button
                      key={ag}
                      onClick={() => toggleAgeGroup(ag)}
                      data-testid={`wizard-ag-${ag.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        selectedAgeGroups.includes(ag)
                          ? "bg-[#1E2761] text-white border-[#1E2761]"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {ag}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Format</p>
                <div className="flex gap-2">
                  {(["T20", "40-over", "T10"] as Format[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setFormat(f)}
                      data-testid={`wizard-format-${f.toLowerCase().replace("-", "")}`}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        format === f
                          ? "bg-[#1E2761] text-white border-[#1E2761]"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  role="switch"
                  aria-checked={divisionSplit}
                  onClick={() => setDivisionSplit((v) => !v)}
                  data-testid="wizard-division-split-toggle"
                  className={`relative w-10 h-5 rounded-full transition-colors ${divisionSplit ? "bg-[#1E2761]" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${divisionSplit ? "translate-x-5" : ""}`} />
                </button>
                <span className="text-sm text-gray-700">Split into Div A / Div B</span>
              </div>
            </div>
          )}

          {/* ── Step 2 — Division assignment ── */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-xs text-gray-500">Click a player to move them between divisions.</p>
              {selectedAgeGroups.map((ag) => {
                const { divA, divB } = divTeams[ag] ?? { divA: [], divB: [] };
                return (
                  <div key={ag} className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">{ag}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-semibold text-blue-700 mb-1.5">Div A</p>
                        <div className="space-y-1">
                          {divA.map((team) => (
                            <button
                              key={team}
                              onClick={() => moveToDiv(ag, team, "divA")}
                              className="w-full text-left px-2.5 py-1.5 text-xs rounded-lg bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100 transition-colors"
                            >
                              {team} →
                            </button>
                          ))}
                          {divA.length === 0 && <p className="text-xs text-gray-400 italic">Empty</p>}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-purple-700 mb-1.5">Div B</p>
                        <div className="space-y-1">
                          {divB.map((team) => (
                            <button
                              key={team}
                              onClick={() => moveToDiv(ag, team, "divB")}
                              className="w-full text-left px-2.5 py-1.5 text-xs rounded-lg bg-purple-50 border border-purple-200 text-purple-800 hover:bg-purple-100 transition-colors"
                            >
                              ← {team}
                            </button>
                          ))}
                          {divB.length === 0 && <p className="text-xs text-gray-400 italic">Empty</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Step 3 — Schedule constraints ── */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="wizard-start-date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761]"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="wizard-end-date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2761]"
                  />
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Grounds</p>
                <div className="space-y-2">
                  {ALL_GROUNDS.map((g) => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedGrounds.includes(g)}
                        onChange={() => toggleGround(g)}
                        data-testid={`wizard-ground-${g.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded border-gray-300 text-[#1E2761]"
                      />
                      <span className="text-sm text-gray-700">{g}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Games per Day (per ground)</p>
                <div className="flex gap-2">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      onClick={() => setGamesPerDay(n)}
                      data-testid={`wizard-gpd-${n}`}
                      className={`w-10 h-10 rounded-lg text-sm font-semibold border transition-colors ${
                        gamesPerDay === n
                          ? "bg-[#1E2761] text-white border-[#1E2761]"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 4 — Preview ── */}
          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">{previewFixtures.length} fixtures generated · {format} · {selectedAgeGroups.join(", ")}</p>
              <div className="rounded-xl border border-gray-200 overflow-hidden max-h-64 overflow-y-auto" data-testid="wizard-preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Date</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Teams</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Ground</th>
                      {divisionSplit && <th className="text-left px-3 py-2 font-semibold text-gray-700">Div</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewFixtures.map((f) => (
                      <tr key={f.id} data-testid={`preview-row-${f.id}`}>
                        <td className="px-3 py-2 text-gray-500 font-mono">{f.date}</td>
                        <td className="px-3 py-2 text-gray-800 font-medium">{f.teamA} <span className="text-gray-400 font-normal">vs</span> {f.teamB}</td>
                        <td className="px-3 py-2 text-gray-500">{f.ground}</td>
                        {divisionSplit && (
                          <td className="px-3 py-2">
                            <Badge className={`text-xs border ${f.div === "A" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
                              Div {f.div}
                            </Badge>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer nav */}
          <div className="flex justify-between pt-4 border-t border-gray-100 mt-4">
            <Button variant="outline" size="sm" onClick={step === 1 ? () => setWizardOpen(false) : handleBack} data-testid="wizard-btn-back">
              {step === 1 ? "Cancel" : "← Back"}
            </Button>
            <div className="flex gap-2">
              {step === 4 && (
                <Button variant="outline" size="sm" onClick={handleRegenerate} data-testid="wizard-btn-regenerate">
                  Regenerate
                </Button>
              )}
              {step < 4 ? (
                <Button
                  size="sm"
                  className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                  onClick={handleNext}
                  disabled={step === 1 ? !canNext1 : step === 3 ? !canNext3 : false}
                  data-testid="wizard-btn-next"
                >
                  Next →
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                  onClick={handleConfirm}
                  data-testid="wizard-btn-confirm"
                >
                  Confirm Fixtures
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </HubAdminDemoLayout>
  );
}

// ─── Fixture card ─────────────────────────────────────────────────────────────

function FixtureCard({ fixture: f }: { fixture: Fixture }) {
  const isUpcoming = f.status === "upcoming" || f.status === "scheduled";
  return (
    <div
      className={`rounded-xl border border-gray-200 px-5 py-4 ${isUpcoming && f.status !== "scheduled" ? "bg-blue-50 border-blue-200" : "bg-white"}`}
      data-testid={`fixture-card-${f.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="shrink-0 text-center sm:w-24">
          <p className="text-xs font-semibold text-gray-800">{f.date}</p>
          <p className="text-xs text-gray-500">{f.time}</p>
          <span className="mt-1 inline-block text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">{f.ageGroup}</span>
        </div>

        <div className="flex-1 flex items-center justify-center gap-3 text-center">
          <p className="font-semibold text-gray-900 text-sm flex-1 text-right">{f.teamA}</p>
          <span className="text-xs font-bold text-gray-400 shrink-0">vs</span>
          <p className="font-semibold text-gray-900 text-sm flex-1 text-left">{f.teamB}</p>
        </div>

        <div className="shrink-0 text-right space-y-1">
          <p className="text-xs text-gray-500">{f.ground}</p>
          <div className="flex items-center gap-1 justify-end">
            {f.div && (
              <Badge className={`text-xs border ${f.div === "A" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
                Div {f.div}
              </Badge>
            )}
            <Badge className={`border text-xs ${STATUS_BADGE[f.status] ?? STATUS_BADGE.scheduled}`}>
              {f.status}
            </Badge>
          </div>
        </div>
      </div>

      {f.result && (
        <p className="mt-2 text-xs text-green-700 font-medium border-t border-green-100 pt-2">
          {f.result}
        </p>
      )}
    </div>
  );
}
